import { Scene, Vector3, Ray, AbstractMesh, Color3 } from '@babylonjs/core';
import { logger } from '../core/Logger';
import { ProjectileInstance, HitResult, ProjectileSpec } from '../types/combat';
import type { FXConfig } from './effects/FXPresets';
import type { Character } from './Character';
import type { ParticleManager } from './ParticleManager';
import { createProjectileMesh } from './MeshFactory';
import type { ExtractionResult } from '../assets/EntityAssetExtractor';
import { UnifiedLightManager, type LightHandle } from './UnifiedLightManager';

export interface ProjectileHitResult {
  projectileId: string;
  targetId: string;
  position: Vector3;
  normal?: Vector3;
  damage: number;
  attackDefId: string;
  impactDirection?: Vector3;
  impactFX?: FXConfig; // Impact FX config to spawn on hit
}

export interface ProjectileSpawnParams {
  ownerId: string;
  attackDefId: string;
  spec: ProjectileSpec;
  position: Vector3;
  velocity: Vector3;
  direction: Vector3;
  mesh?: AbstractMesh | null;
  embeddedEntities?: ExtractionResult;
  damage: number;
  impactFX?: FXConfig; // Impact FX config to spawn on hit
}

/**
 * ProjectileSystem manages all active projectiles: spawning, movement, hit detection, destruction.
 * Does NOT apply damage directly - returns hit results to CombatSystem for processing.
 */
export class ProjectileSystem {
  private scene: Scene;
  private activeProjectiles: Map<string, ProjectileInstance> = new Map();
  private particleManager: ParticleManager | null = null;
  private nextInstanceId = 0;
  
  /** Light handles for projectiles - stored separately to avoid modifying ProjectileInstance type */
  private lightHandles: Map<string, LightHandle> = new Map();
  
  /** Get time in seconds - injected from CombatSystem */
  private getTimeSeconds: () => number;
  
  /** Raycast function - injected to avoid circular dependency */
  private raycastCharacters: (ray: Ray, excludeOwnerId: string, characters: Character[], hitRadius?: number) => HitResult;

  constructor(
    scene: Scene,
    getTimeSeconds: () => number,
    raycastCharacters: (ray: Ray, excludeOwnerId: string, characters: Character[], hitRadius?: number) => HitResult
  ) {
    this.scene = scene;
    this.getTimeSeconds = getTimeSeconds;
    this.raycastCharacters = raycastCharacters;
  }

  setParticleManager(pm: ParticleManager): void {
    this.particleManager = pm;
  }

  /**
   * Spawn a new projectile.
   */
  spawn(params: ProjectileSpawnParams): ProjectileInstance | null {
    const instanceId = this.generateInstanceId();
    const { spec, position, velocity, mesh, embeddedEntities, ownerId, attackDefId, damage, impactFX } = params;
    
    logger.core.debug(`ProjectileSystem: Spawning ${spec.type} projectile ${instanceId} for ${ownerId}`);
    
    // Load projectile mesh or create from spec
    let projectileMesh: AbstractMesh | null = mesh ?? null;
    if (projectileMesh) {
      projectileMesh.name = `projectile_${instanceId}`;
      const detach = (projectileMesh as unknown as { detachFromBone?: () => void }).detachFromBone;
      if (typeof detach === 'function') {
        detach.call(projectileMesh);
      } else {
        projectileMesh.parent = null;
      }
    } else {
      projectileMesh = createProjectileMesh(this.scene, spec, `projectile_${instanceId}`, embeddedEntities);
    }

    projectileMesh.position.copyFrom(position);
    
    // Orient projectile to face the velocity direction
    if (velocity.lengthSquared() > 1e-6) {
      const forward = velocity.normalizeToNew();
      const lookTarget = position.add(forward);
      projectileMesh.lookAt(lookTarget);
    }
    
    const projectileInstance: ProjectileInstance & { damage?: number } = {
      id: instanceId,
      ownerId,
      attackDefId,
      spec,
      mesh: projectileMesh,
      position: position.clone(),
      velocity: velocity.clone(),
      spawnTime: this.getTimeSeconds(),
      spawnPosition: position.clone(),
      lastPosition: position.clone(),
    };
    // Store damage and impactFX for hit results (extended properties)
    (projectileInstance as any).damage = damage;
    (projectileInstance as any).impactFX = impactFX;
    
    // Create attached light if specified (using UnifiedLightManager for combat lights)
    if (spec.light) {
      const handle = UnifiedLightManager.acquireCombatLight({
        position: position.clone(),
        color: spec.light.color ?? new Color3(1, 1, 1),
        intensity: spec.light.intensity ?? 1.0,
        range: spec.light.range ?? 5.0
      });
      
      if (handle) {
        // Store light offset for position updates
        (projectileInstance as any).lightOffset = spec.light.offset?.clone() ?? Vector3.Zero();
        // NOTE: Pool lights cannot be parented - position is updated via handle.update() each frame
        this.lightHandles.set(instanceId, handle);
      } else {
        logger.core.warn(`ProjectileSystem: UnifiedLightManager failed for projectile ${instanceId}`);
      }
    }
    
    // For ballistic projectiles, set up fuse timer
    if (spec.type === 'ballistic' && spec.fuseTime !== undefined) {
      projectileInstance.fuseStartTime = projectileInstance.spawnTime;
    }
    
    this.activeProjectiles.set(instanceId, projectileInstance);
    logger.core.debug(`ProjectileSystem: Projectile ${instanceId} spawned at ${position}`);
    return projectileInstance;
  }

  /**
   * Update all projectiles. Returns hit results for CombatSystem to process.
   */
  update(deltaTime: number, characters: Character[]): ProjectileHitResult[] {
    const sceneTime = this.getTimeSeconds();
    const hitResults: ProjectileHitResult[] = [];
    const toDestroy: string[] = [];
    const toDetonate: ProjectileInstance[] = [];

    for (const [_id, projectile] of this.activeProjectiles) {
      const result = this.updateProjectile(projectile, deltaTime, sceneTime, characters);
      
      if (result.destroy) {
        toDestroy.push(projectile.id);
      }
      if (result.detonate) {
        toDetonate.push(projectile);
      }
      if (result.hit) {
        hitResults.push(result.hit);
        toDestroy.push(projectile.id);
      }
    }

    // Handle detonations (area damage returned as hit results)
    for (const projectile of toDetonate) {
      const detonationHits = this.detonateProjectile(projectile, characters);
      hitResults.push(...detonationHits);
    }

    // Clean up destroyed projectiles
    for (const id of toDestroy) {
      this.destroy(id);
    }

    return hitResults;
  }

  /**
   * Update a single projectile.
   */
  private updateProjectile(
    projectile: ProjectileInstance,
    deltaTime: number,
    sceneTime: number,
    characters: Character[]
  ): { destroy?: boolean; detonate?: boolean; hit?: ProjectileHitResult } {
    // Check lifetime expiration
    const lifetime = sceneTime - projectile.spawnTime;
    const maxLifetime = projectile.spec.maxLifetime ?? 10;
    if (lifetime > maxLifetime) {
      return { destroy: true };
    }
    
    // Check max distance expiration
    const distanceTraveled = Vector3.Distance(projectile.position, projectile.spawnPosition);
    const maxDistance = projectile.spec.maxDistance ?? 20;
    if (distanceTraveled > maxDistance) {
      logger.core.debug(`ProjectileSystem: Projectile ${projectile.id} exceeded max distance`);
      return { destroy: true };
    }

    // Check fuse timer for timed detonation
    if (projectile.spec.fuseTime !== undefined && projectile.fuseStartTime !== undefined) {
      const fuseElapsed = sceneTime - projectile.fuseStartTime;
      if (fuseElapsed >= projectile.spec.fuseTime && !projectile.detonated) {
        return { detonate: true };
      }
    }

    // Update movement based on type
    if (projectile.spec.type === 'kinematic') {
      return this.updateKinematicProjectile(projectile, deltaTime, characters);
    } else if (projectile.spec.type === 'ballistic') {
      return this.updateBallisticProjectile(projectile, deltaTime, characters);
    }
    
    return {};
  }

  /**
   * Update kinematic projectile (straight-line raycast).
   */
  private updateKinematicProjectile(
    projectile: ProjectileInstance,
    deltaTime: number,
    characters: Character[]
  ): { hit?: ProjectileHitResult } {
    // Store previous position for segment cast
    projectile.lastPosition.copyFrom(projectile.position);

    // Move projectile
    const displacement = projectile.velocity.scale(deltaTime);
    projectile.position.addInPlace(displacement);
    projectile.mesh.position.copyFrom(projectile.position);

    // Update light position if this projectile has a light attached
    const lightHandle = this.lightHandles.get(projectile.id);
    if (lightHandle) {
      const offset = (projectile as any).lightOffset ?? Vector3.Zero();
      lightHandle.update({ position: projectile.position.add(offset) });
    }

    // Segment cast from last to current position for hit detection
    const direction = projectile.position.subtract(projectile.lastPosition);
    const distance = direction.length();
    if (distance < 0.001) return {};

    const ray = new Ray(projectile.lastPosition, direction.normalize(), distance);
    const hitRadius = projectile.spec.hitRadius ?? 0;
    const hitResult = this.raycastCharacters(ray, projectile.ownerId, characters, hitRadius);

    if (hitResult.hit && hitResult.targetId) {
      const impactDir = projectile.velocity.lengthSquared() > 1e-6
        ? projectile.velocity.normalizeToNew()
        : undefined;

      const damage = (projectile as any).damage ?? projectile.spec.detonationDamage ?? 10;
      const impactFX = (projectile as any).impactFX;

      return {
        hit: {
          projectileId: projectile.id,
          targetId: hitResult.targetId,
          position: hitResult.position!,
          normal: hitResult.normal,
          damage,
          attackDefId: projectile.attackDefId,
          impactDirection: impactDir,
          impactFX,
        }
      };
    }

    return {};
  }

  /**
   * Update ballistic projectile (physics-driven).
   */
  private updateBallisticProjectile(
    projectile: ProjectileInstance,
    _deltaTime: number,
    _characters: Character[]
  ): { hit?: ProjectileHitResult } {
    // Ballistic projectiles use Ammo physics impostor
    if (projectile.physicsImpostor) {
      const physicsPosition = projectile.physicsImpostor.getObjectCenter();
      projectile.position.copyFrom(physicsPosition);
      projectile.mesh.position.copyFrom(physicsPosition);
      
      // Update light position if this projectile has a light attached
      const lightHandle = this.lightHandles.get(projectile.id);
      if (lightHandle) {
        const offset = (projectile as any).lightOffset ?? Vector3.Zero();
        lightHandle.update({ position: projectile.position.add(offset) });
      }
      // TODO: Contact callbacks for collision detection
    }
    return {};
  }

  /**
   * Detonate a projectile (timed explosion). Returns area damage hits.
   */
  private detonateProjectile(projectile: ProjectileInstance, characters: Character[]): ProjectileHitResult[] {
    logger.core.debug(`ProjectileSystem: Detonating projectile ${projectile.id}`);
    projectile.detonated = true;

    const hits: ProjectileHitResult[] = [];

    // Apply area-of-effect damage
    if (projectile.spec.detonationRadius && projectile.spec.detonationDamage) {
      const radius = projectile.spec.detonationRadius;
      const damage = projectile.spec.detonationDamage;
      
      const impactFX = (projectile as any).impactFX;
      
      for (const character of characters) {
        if (character.getId() === projectile.ownerId) continue; // Don't damage self
        
        const charPos = character.character.getAbsolutePosition();
        const distance = Vector3.Distance(projectile.position, charPos);
        
        if (distance <= radius) {
          // Calculate falloff (linear for simplicity)
          const falloff = 1 - (distance / radius);
          const actualDamage = Math.floor(damage * falloff);
          
          if (actualDamage > 0) {
            const impactDir = charPos.subtract(projectile.position).normalize();
            hits.push({
              projectileId: projectile.id,
              targetId: character.getId(),
              position: charPos.clone(),
              damage: actualDamage,
              attackDefId: projectile.attackDefId,
              impactDirection: impactDir,
              impactFX,
            });
          }
        }
      }
    }

    // Schedule destruction (allow animation to play)
    setTimeout(() => this.destroy(projectile.id), 1000);

    return hits;
  }

  /**
   * Destroy a projectile instance.
   */
  destroy(id: string): void {
    const projectile = this.activeProjectiles.get(id);
    if (!projectile) return;

    logger.core.debug(`ProjectileSystem: Destroying projectile ${id}`);

    // Spawn impact particles if configured
    if (projectile.spec.impactParticlePreset && this.particleManager) {
      this.particleManager.spawnOneShot(projectile.spec.impactParticlePreset, projectile.position.clone());
    }

    // Release light handle back to pool
    const lightHandle = this.lightHandles.get(id);
    if (lightHandle) {
      if (lightHandle.light) {
        lightHandle.light.parent = null;
      }
      lightHandle.release();
      this.lightHandles.delete(id);
    } else if (projectile.light) {
      // Fallback for legacy direct lights
      projectile.light.parent = null;
      projectile.light.dispose();
    }

    // Dispose mesh
    projectile.mesh.dispose();
    if (projectile.collisionMesh) {
      projectile.collisionMesh.dispose();
    }

    // Dispose physics impostor
    if (projectile.physicsImpostor) {
      projectile.physicsImpostor.dispose();
    }

    this.activeProjectiles.delete(id);
  }

  /**
   * Get all active projectiles for debug display.
   */
  getActiveProjectiles(): ProjectileInstance[] {
    return Array.from(this.activeProjectiles.values());
  }

  /**
   * Get serializable projectile info for debug UI.
   */
  getDebugInfo() {
    return Array.from(this.activeProjectiles.values()).map(proj => ({
      id: proj.id,
      ownerId: proj.ownerId,
      attackDefId: proj.attackDefId,
      type: proj.spec.type,
      position: { x: proj.position.x, y: proj.position.y, z: proj.position.z },
      velocity: { x: proj.velocity.x, y: proj.velocity.y, z: proj.velocity.z },
      hasCollided: proj.hasCollided ?? false,
      detonated: proj.detonated ?? false,
    }));
  }

  /**
   * Clean up all projectiles (level unload).
   */
  dispose(): void {
    for (const id of this.activeProjectiles.keys()) {
      this.destroy(id);
    }
  }

  private generateInstanceId(): string {
    return `projectile_${this.nextInstanceId++}`;
  }
}
